import { onMounted, onUnmounted } from 'vue';
import type * as t from 'io-ts';

import { socket } from '@/scripts/socket';
import { decodeAsTypeC } from '@/scripts/typeAssertions/isTypeC';

export enum Channel {
    RECORDING = 'recording:status',
    COMMENT_INTELLIGENCE = 'comment-intelligence:status',
    RECORDING_PUBLIC = 'recording:status:public',
    STREAM = 'stream:status',
    RECORD = 'record',
    RECORD_CUT = 'record:cut',
    SCHEDULE = 'schedule',
    REPORT = 'report',
    EXCEPTION = 'exception',
    TRANSLATION_UPDATE = 'translation:update',
    GENERATED_MEDIA_UPDATE = 'generated-media:update',
    WORKLOAD_QUEUE_UPDATE = 'workload:queue:update',
    LLM_AVAILABILITY = 'llm:availability',
    ENTITY_UPDATE = 'entity:update',
    TRANSCRIPT = 'transcript:status',
    TRANSCRIPT_SPEAKERS_UPDATE = 'transcript:speakers:update',
    OCR = 'ocr:status',
    KEYWORD_SCAN = 'keyword:scan',
    DYSFLUENCY = 'dysfluency:status',
    SCENE = 'scene:status',
    SENTIMENT = 'sentiment:status',
    COMMENT_ADD = 'comment:add',
    COMMENT_EDIT = 'comment:edit',
    COMMENT_DELETE = 'comment:delete',
    NOTIFICATION = 'notification',
    ALERT = 'alert',
    SETTINGS = 'settings',
    ROOM = 'room',
    ROOM_EDIT = 'room:edit',
    ROOM_ADD = 'room:add',
    ROOM_DELETE = 'room:delete',
    CIRCUIT = 'circuit',
    CUE_ACTIVITY = 'cue:activity',
    CUE_FLOW = 'cue:flow',
    WOWZA_LOW_SPACE = 'wowza:low-space',
    USER_LOGOUT_SOON = 'user:logout-soon',
}

type Handler = (json: string) => void;

export const useSocket = (channel: Channel, handler: Handler) => {
    onMounted(() => {
        socket.on(channel, handler);
    });

    onUnmounted(() => {
        socket.off(channel, handler);
    });
};

/**
 * Runs the handler every time the socket (re)connects.
 *
 * A subscription alone misses everything published while the connection was
 * down, so a view that trusts the socket has to re-read its state on the way
 * back up.
 */
export const useSocketReconnect = (handler: () => void) => {
    onMounted(() => {
        socket.on('connect', handler);
    });

    onUnmounted(() => {
        socket.off('connect', handler);
    });
};

type TypedHandler<T extends t.Any> = (message: t.TypeOf<T>) => void;

export const useTypedSocket = <T extends t.Any>(
    channel: Channel,
    type: T,
    handler: TypedHandler<T>,
) => {
    const typedHandler = (json: string): void => {
        const parsedJson = JSON.parse(json);
        const message = decodeAsTypeC(parsedJson, type);

        handler(message);
    };

    return useSocket(channel, typedHandler);
};
